import BasePlugin from '../_base';
import { registerPlugin } from '../../plugins';
import EventManager from '../../eventManager';
import { addClass, removeClass, closest } from '../../helpers/dom/element';
import { arrayEach } from '../../helpers/array';
import RowsMapper from './rowsMapper';
import FilterPanel from './filterPanel';
import { matchState, normalizeValue, isStateActive } from './conditions';

/**
 * @plugin ColumnFilters
 *
 * @description
 * 列筛选插件。为每列表头提供一个筛选入口，支持：
 *   1. 值筛选：列出该列全部去重值，勾选保留哪些（含"(空白)"）；
 *   2. 条件筛选：包含/等于/大于/介于 等 14 种算子；
 *   3. 二者为"与"关系，多列筛选之间同样为"与"关系。
 *
 * 实现方式与 columnSorting 一致：通过 `modifyRow` 钩子把可视行号翻译为
 * 物理行号，并用 `RowsMapper` 只保留通过筛选的物理行，因此筛选不修改
 * 任何源数据，清除筛选后数据原样恢复。
 *
 * @example
 * ```js
 * const hot = new Handsontable(element, {
 *   data: getData(),
 *   colHeaders: true,
 *   columnFilters: true,
 * });
 *
 * // 通过 API 操作
 * const plugin = hot.getPlugin('columnFilters');
 * plugin.setFilterState(0, { selectedValues: ['A'] });
 * plugin.clearFilterState(0);
 * plugin.clearAllFilters();
 * ```
 */
class ColumnFilters extends BasePlugin {
  constructor(hotInstance) {
    super(hotInstance);

    /**
     * 各列筛选状态，键为物理列下标。
     *
     * @type {Object}
     */
    this.filterStates = {};

    /**
     * 行映射器：可视行号 -> 物理行号。
     *
     * @type {RowsMapper}
     */
    this.rowsMapper = new RowsMapper();

    /**
     * 筛选面板。
     *
     * @type {FilterPanel}
     */
    this.panel = null;

    /**
     * 防止插件自身读取数据时被 modifyRow 再次翻译，造成递归。
     *
     * @type {Boolean}
     */
    this.blockTranslation = false;

    this.eventManager = new EventManager(this);
  }

  /**
   * 检查插件是否应启用（对应 `columnFilters` 配置项）。
   *
   * @returns {Boolean}
   */
  isEnabled() {
    return !!this.hot.getSettings().columnFilters;
  }

  enablePlugin() {
    if (this.enabled) {
      return;
    }

    this.panel = new FilterPanel({
      onConfirm: (column, state) => this.onPanelConfirm(column, state),
      onClear: column => this.onPanelClear(column),
    });

    this.addHook('modifyRow', (row, source) => this.onModifyRow(row, source));
    this.addHook('unmodifyRow', (row, source) => this.onUnmodifyRow(row, source));
    this.addHook('afterGetColHeader', (column, TH) => this.onAfterGetColHeader(column, TH));
    this.addHook('afterLoadData', () => this.onAfterLoadData());
    this.addHook('afterCreateRow', (index, amount) => this.onAfterCreateRow(index, amount));
    this.addHook('afterRemoveRow', (index, amount) => this.onAfterRemoveRow(index, amount));
    // 表头点击（含筛选图标）由 mousedown 捕获，避免与列排序/选列冲突
    this.addHook('afterOnCellMouseDown', (event, coords, TD) => this.onAfterOnCellMouseDown(event, coords, TD));

    this.rowsMapper.createMap(this.hot.countSourceRows());

    // 点击表格外部时关闭面板
    this.onDocumentMouseDown = (event) => {
      if (!this.panel || !this.panel.isVisible()) {
        return;
      }
      if (this.panel.element.contains(event.target)) {
        return;
      }
      // 点在筛选图标上时由图标自身逻辑处理（toggle）
      if (closest(event.target, ['TH'])
        && event.target.className
        && String(event.target.className).indexOf('htFilterIcon') !== -1) {
        return;
      }
      this.panel.hide();
    };
    this.eventManager.addEventListener(document, 'mousedown', this.onDocumentMouseDown);

    // 面板挂在 body 上、按视口坐标 fixed 定位，因此任何祖先容器（含表格自身的
    // 横纵向滚动区）滚动后位置都会失效；统一在滚动/缩放时收起面板。
    // 用捕获阶段监听才能拿到内部容器的滚动事件（scroll 不冒泡）。
    this.onViewportChange = () => {
      if (this.panel && this.panel.isVisible()) {
        this.panel.hide();
      }
    };
    document.addEventListener('scroll', this.onViewportChange, true);
    window.addEventListener('resize', this.onViewportChange);

    super.enablePlugin();
  }

  disablePlugin() {
    this.filterStates = {};
    this.rowsMapper.clearMap();

    if (this.onViewportChange) {
      document.removeEventListener('scroll', this.onViewportChange, true);
      window.removeEventListener('resize', this.onViewportChange);
      this.onViewportChange = null;
    }

    if (this.panel) {
      this.panel.destroy();
      this.panel = null;
    }

    super.disablePlugin();
  }

  /**
   * 设置某列的筛选状态并重新计算。
   *
   * @param {Number} column 可视列下标
   * @param {Object} state 筛选状态 { selectedValues, condition }
   */
  setFilterState(column, state) {
    const physicalColumn = this.hot.toPhysicalColumn(column);

    if (!state || (state.selectedValues === null && !state.condition)) {
      delete this.filterStates[physicalColumn];
    } else {
      this.filterStates[physicalColumn] = state;
    }

    this.applyFilters();
  }

  /**
   * 获取某列筛选状态。
   *
   * @param {Number} column 可视列下标
   * @returns {Object|null}
   */
  getFilterState(column) {
    const physicalColumn = this.hot.toPhysicalColumn(column);

    return this.filterStates[physicalColumn] || null;
  }

  /**
   * 清除某列筛选。
   *
   * @param {Number} column 可视列下标
   */
  clearFilterState(column) {
    const physicalColumn = this.hot.toPhysicalColumn(column);

    delete this.filterStates[physicalColumn];
    this.applyFilters();
  }

  /**
   * 清除全部筛选。
   */
  clearAllFilters() {
    this.filterStates = {};
    this.applyFilters();
  }

  /**
   * 当前是否存在生效中的筛选。
   *
   * @returns {Boolean}
   */
  isFiltered() {
    return Object.keys(this.filterStates).length > 0;
  }

  /**
   * 返回通过筛选的物理行号数组。
   *
   * @returns {Number[]}
   */
  getFilteredRows() {
    return this.rowsMapper._arrayMap.slice();
  }

  /**
   * 按当前 filterStates 重算行映射并重绘。
   */
  applyFilters() {
    const totalRows = this.hot.countSourceRows();
    const physicalColumns = Object.keys(this.filterStates);

    if (!physicalColumns.length) {
      this.rowsMapper.createMap(totalRows);
      this.commit();

      return;
    }

    const passed = [];

    // 读取源数据时禁用行翻译，确保按物理行号取值
    this.blockTranslation = true;

    for (let physicalRow = 0; physicalRow < totalRows; physicalRow++) {
      let matched = true;

      for (let i = 0; i < physicalColumns.length; i++) {
        const physicalColumn = parseInt(physicalColumns[i], 10);
        const state = this.filterStates[physicalColumn];
        const value = this.getSourceValue(physicalRow, physicalColumn);

        if (!matchState(value, state)) {
          matched = false;
          break;
        }
      }

      if (matched) {
        passed.push(physicalRow);
      }
    }

    this.blockTranslation = false;

    this.rowsMapper.setMap(passed);
    this.commit();
  }

  /**
   * 让行数变化生效并重绘。
   *
   * countRows() 走 dataMap.getLength()，其结果带缓存（cachedLength），
   * 必须先通过官方的 `skipLengthCache` 钩子失效缓存，否则行数不会更新。
   *
   * @private
   */
  commit() {
    this.hot.runHooks('skipLengthCache', 0);

    // 筛选后原选区可能已越界，先清掉避免渲染异常
    this.hot.deselectCell();
    this.hot.render();
    this.refreshHeaders();
  }

  /**
   * 按物理行列号读取源数据值。
   *
   * @param {Number} physicalRow
   * @param {Number} physicalColumn
   * @returns {*}
   */
  getSourceValue(physicalRow, physicalColumn) {
    // getSourceDataAtCell 接受物理行号与可视列号；此处列未做映射变换，
    // 故直接使用 physicalColumn（与 columns 配置顺序一致）
    return this.hot.getSourceDataAtCell(physicalRow, physicalColumn);
  }

  /**
   * 收集某列的全部去重值（用于面板值列表）。
   *
   * @param {Number} column 可视列下标
   * @returns {String[]}
   */
  collectDistinctValues(column) {
    const physicalColumn = this.hot.toPhysicalColumn(column);
    const totalRows = this.hot.countSourceRows();
    const seen = {};
    const values = [];

    this.blockTranslation = true;

    for (let physicalRow = 0; physicalRow < totalRows; physicalRow++) {
      const raw = this.getSourceValue(physicalRow, physicalColumn);
      const value = normalizeValue(raw);

      if (!seen[value]) {
        seen[value] = true;
        values.push(value);
      }
    }

    this.blockTranslation = false;

    // 空值排最前，其余按自然顺序（数字优先按数值比较）
    return values.sort((a, b) => {
      if (a === '') return -1;
      if (b === '') return 1;

      const numA = Number(a);
      const numB = Number(b);

      if (!isNaN(numA) && !isNaN(numB)) {
        return numA - numB;
      }

      return a.localeCompare(b, 'zh-CN');
    });
  }

  /**
   * 打开/关闭某列筛选面板。
   *
   * @param {Number} column 可视列下标
   * @param {HTMLElement} th 表头元素
   */
  togglePanel(column, th) {
    if (!this.panel) {
      return;
    }

    if (this.panel.isVisible() && this.panel.column === column) {
      this.panel.hide();

      return;
    }

    // 面板挂载到 document.body 并使用 fixed 定位，不受表格容器 overflow 裁切影响
    this.panel.show(
      column,
      th,
      this.collectDistinctValues(column),
      this.getFilterState(column),
    );
  }

  onPanelConfirm(column, state) {
    this.setFilterState(column, state);
    this.panel.hide();
  }

  onPanelClear(column) {
    this.clearFilterState(column);
    this.panel.hide();
  }

  /**
   * `modifyRow` 回调：把可视行号翻译成物理行号。
   *
   * 关键约定（见 dataMap.getLength）：对"被筛掉而不存在"的可视行必须返回
   * null，dataMap 依此递减 countRows()，表格才会真正只显示通过筛选的行。
   *
   * @private
   * @param {Number} row 可视行号
   * @param {String} source
   * @returns {Number|null} 物理行号；越界（被筛掉）时为 null
   */
  onModifyRow(row, source) {
    if (this.blockTranslation || source === this.pluginName || !this.isFiltered()) {
      return row;
    }

    const physicalRow = this.rowsMapper.getValueByIndex(row);

    // getValueByIndex 对超出映射长度的下标返回 null，正是"该行已被筛掉"，
    // 直接透传 null 让 dataMap 递减行数。
    return physicalRow;
  }

  /**
   * `unmodifyRow` 回调：把物理行号翻译回可视行号。
   *
   * @private
   * @param {Number} row 物理行号
   * @param {String} source
   * @returns {Number} 可视行号
   */
  onUnmodifyRow(row, source) {
    if (this.blockTranslation || source === this.pluginName || !this.isFiltered()) {
      return row;
    }

    const visualRow = this.rowsMapper.getIndexByValue(row);

    return visualRow === null ? row : visualRow;
  }

  /**
   * 在表头注入筛选图标。
   *
   * @private
   * @param {Number} column 可视列下标
   * @param {HTMLElement} TH
   */
  onAfterGetColHeader(column, TH) {
    if (column < 0 || !TH) {
      return;
    }

    const host = TH.querySelector('.relative') || TH;
    let icon = host.querySelector('.htFilterIcon');

    if (!icon) {
      icon = document.createElement('span');
      icon.className = 'htFilterIcon';
      icon.setAttribute('title', '筛选');
      host.appendChild(icon);
    }

    icon.setAttribute('data-column', String(column));

    const state = this.getFilterState(column);
    // 判断是否"生效中"需要知道该列去重值总数；为避免每次渲染都全表扫描，
    // 这里只在存在状态时才计算
    const active = state
      ? isStateActive(state, this.collectDistinctValuesCount(column))
      : false;

    if (active) {
      addClass(icon, 'htFilterIconActive');
    } else {
      removeClass(icon, 'htFilterIconActive');
    }
  }

  /**
   * 获取某列去重值数量（带缓存，避免表头重绘时反复全表扫描）。
   *
   * @private
   * @param {Number} column
   * @returns {Number}
   */
  collectDistinctValuesCount(column) {
    if (!this.distinctCountCache) {
      this.distinctCountCache = {};
    }

    const physicalColumn = this.hot.toPhysicalColumn(column);

    if (this.distinctCountCache[physicalColumn] === undefined) {
      this.distinctCountCache[physicalColumn] = this.collectDistinctValues(column).length;
    }

    return this.distinctCountCache[physicalColumn];
  }

  /**
   * 表头 mousedown：点中筛选图标时打开面板并阻止后续选列/排序。
   *
   * @private
   */
  onAfterOnCellMouseDown(event, coords, TD) {
    const target = event.target;

    if (!target || !target.className
      || String(target.className).indexOf('htFilterIcon') === -1) {
      return;
    }

    event.stopImmediatePropagation();
    event.preventDefault();

    const column = parseInt(target.getAttribute('data-column'), 10);
    const th = closest(target, ['TH']);

    if (!isNaN(column) && th) {
      this.togglePanel(column, th);
    }
  }

  /**
   * 数据整体替换后重置筛选。
   *
   * @private
   */
  onAfterLoadData() {
    this.filterStates = {};
    this.distinctCountCache = {};
    this.rowsMapper.createMap(this.hot.countSourceRows());
  }

  /**
   * 新增行：映射表右移，新行默认可见。
   *
   * @private
   */
  onAfterCreateRow(index, amount) {
    this.distinctCountCache = {};

    if (!this.isFiltered()) {
      this.rowsMapper.createMap(this.hot.countSourceRows());

      return;
    }

    this.rowsMapper.shiftItems(index, amount);
  }

  /**
   * 删除行：映射表左移。
   *
   * @private
   */
  onAfterRemoveRow(index, amount) {
    this.distinctCountCache = {};

    if (!this.isFiltered()) {
      this.rowsMapper.createMap(this.hot.countSourceRows());

      return;
    }

    this.rowsMapper.unshiftItems(index, amount);
  }

  /**
   * 强制刷新表头（用于同步筛选图标高亮态）。
   *
   * @private
   */
  refreshHeaders() {
    this.distinctCountCache = {};

    if (this.hot.view && this.hot.view.wt) {
      this.hot.view.wt.draw(true);
    }
  }

  destroy() {
    if (this.rowsMapper) {
      this.rowsMapper.destroy();
    }
    if (this.panel) {
      this.panel.destroy();
      this.panel = null;
    }

    super.destroy();
  }
}

registerPlugin('columnFilters', ColumnFilters);

export default ColumnFilters;
